import { useSelector } from "@/hooks/useSelector";
import { createContext, PropsWithChildren, useCallback, useContext, useEffect, useState } from "react";
import Joyride, { Callback, Step } from "react-joyride";
import { year1 } from "./instructions";
import { Button, Card, CardFooter, Modal, Stack, Typography } from "@prometheus/components";

export interface JoyrideContextValue {
  riding: boolean;
}

export interface JoyrideState {

}

export const JoyrideContext = createContext<JoyrideContextValue>(null!)

export const JoyrideProvider = ({ children }: PropsWithChildren) => {
  const [riding, setRiding] = useState(false)
  const [steps, setSteps] = useState<Step[]>()
  const [stepIndex, setStepIndex] = useState(0);
  const turn = useSelector(s => s.game.metadata?.turn ?? 0);
  const status = useSelector(s => s.game.game?.status);
  const tutorial = useSelector(s => s.game.game?.tutorial);

  const [intro, setIntro] = useState(false);
  const [introStep, setIntroStep] = useState(0);

  const callback: Callback = useCallback((e) => {
    switch (e.action) {
      case "init":
      case "start":
      case "stop":
      case "reset":
      case "go":
      case "close":
      case "skip":
      case "update":
        break;
      case "prev":
        if (e.type === 'step:after') {
          setStepIndex(e.index - 1)
        }
        break;
      case "next":
        if (e.type === 'step:after') {
          setStepIndex(e.index + 1)
        }
        if (e.type === 'tour:end') {
          setRiding(false)
        }
        break;
    }
  }, []);

  useEffect(() => {
    if (steps?.length) {
      setRiding(true)
    } else {
      setRiding(false)
    }
  }, [steps])

  useEffect(() => {
    if (!tutorial) {
      return;
    }

    if (status === 'active' && turn === 1) {
      setIntro(true)
    }
  }, [turn, status])

  return (
    <JoyrideContext.Provider value={{ riding }}>
      {children}
      <Joyride
        styles={{
          buttonNext: {
            color: 'black',
          },
          // tooltip: {
          //   backgroundColor: 'var(--prometheus-bg-primary)',
          //   color: 'var(--prometheus-text-primary)',
          // },
          options: {
            arrowColor: 'var(--prometheus-bg-primary)',
            backgroundColor: 'var(--prometheus-bg-primary)',
            primaryColor: 'var(--prometheus-primary)',
            textColor: 'var(--prometheus-text-primary)',
          }
        }}
        hideCloseButton
        continuous={true}
        run={riding}
        steps={steps}
        stepIndex={stepIndex}
        callback={callback}
      />
      {intro && (
        <Modal onClose={() => {}}>
          <Card style={{ width: '600px' }}>
            <Stack>
              {introStep === 0 && (
                <>
                  <Typography variant="h1" retro color="primary">PROMETHEUS TUTORIAL</Typography>
                  <Typography variant="h4">Welcome to the Prometheus Tutorial!</Typography>
                  <Typography>We will play a short game explaining some of the core mechanics of the UI and game itself.</Typography>
                  <Card padding="sm" borderColor="secondary">
                    <Typography variant="body2">The tutorial is a <strong>fixed experience</strong> that takes you through a basic explanation of most of the mechanics, but does not offer any of the freedom to make your own choices that is provided during regular play.</Typography>
                  </Card>
                </>
              )}
              {introStep === 1 && (
                <>
                  <Typography variant="h2" retro color="primary">Objective</Typography>
                  <Typography>Prometheus is a multiplayer space monopoly styled game with a rich financial economy system. As your empire grows, you are faced with economic recessions, natural disasters, and evil space pirates.</Typography>
                  <Typography>Grow your empire by purchasing planets and transferring your ship crew to the planet. There, they will populate and increase your yearly income over time.</Typography>
                  <Typography>Invest extra cash in the market by trading for resources, or investing in bonds.</Typography>
                  <Typography>Borrow cash from your Line of Credit to purchase financial opportunities that can out perform your LOC Interest Rates!</Typography>
                </>
              )}
              {introStep === 2 && (
                <>
                  <Typography variant="h2" retro color="primary">Win Conditions</Typography>
                  <Typography>If playing a multiplayer game, the objective is to hold 90% of the total owned equity in the game.</Typography>
                  <Typography>If you are playing alone, think of this game more as a survival game, trying to survive recessions and disasters, by leveraging your finances and making good decisions.</Typography>
                </>
              )}
              {introStep === 3 && (
                <>
                  <Typography variant="h2" retro color="primary">Prometheus</Typography>
                  <Typography>Let's start the tutorial!</Typography>
                </>
              )}
            </Stack>
            <CardFooter>
              {introStep !== 3 && (
                <Button variant="primary" onClick={() => setIntroStep(s => s + 1)} outline>Next</Button>
              )}
              {introStep === 3 && (
                <Button variant="primary" onClick={() => { setIntro(false); setSteps(year1); }} outline>Get Started!</Button>
              )}
            </CardFooter>
          </Card>
        </Modal>
      )}
    </JoyrideContext.Provider>
  )
}

export const useJoyride = () => useContext(JoyrideContext)
